import type { Appointment } from '@/api/appointments.api';

/**
 * Geometry and date arithmetic for the calendar.
 *
 * Kept out of the components so it can be tested without rendering anything —
 * overlap layout and week arithmetic are where calendars go quietly wrong.
 */

/** Where the grid starts and ends, in minutes from midnight. */
export const GRID_START = 8 * 60;
export const GRID_END = 20 * 60;
/**
 * Pixels per minute at zoom 1 — an hour is 55px, as the reference draws it.
 *
 * It was 60, which is a rounder number and the wrong one: on the same screen
 * the clinic saw one hour less of its own day than it used to.
 */
export const PX_PER_MIN = 55 / 60;

export function minutesToLabel(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function parseTime(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** Local `YYYY-MM-DD`. Never via toISOString — that shifts to UTC. */
export function toIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function fromIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  // Constructed in local time so the date shown is the date stored.
  return new Date(y, m - 1, d);
}

export function addDays(iso: string, days: number): string {
  const d = fromIsoDate(iso);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

/**
 * The dates of a series the server was asked for and did not book.
 *
 * The API places a series the way `seriesDates` does on its side — the first
 * date, then `repeats` more every `everyWeeks` weeks — and skips a later date
 * whose slot is taken rather than failing the whole series. What it skipped
 * is what was asked for minus what came back.
 */
export function skippedSeriesDates(
  start: string,
  repeats: number,
  everyWeeks: number,
  booked: string[],
): string[] {
  const step = 7 * Math.max(1, everyWeeks);
  const have = new Set(booked);
  const out: string[] = [];
  for (let i = 0; i <= Math.max(0, repeats); i++) {
    const date = addDays(start, i * step);
    if (!have.has(date)) out.push(date);
  }
  return out;
}

/**
 * On which weekday the calendar's week begins: 1 = Monday (the default and
 * Romania's own), 0 = Sunday. A clinic open Sunday–Thursday, or one that simply
 * reads its week Sunday-first, sets the other. Passed explicitly rather than
 * read from a module global so the arithmetic stays pure and testable — the
 * whole reason this file exists apart from the components.
 */
export type WeekStart = 0 | 1;
export const DEFAULT_WEEK_START: WeekStart = 1;

/** First day of the week containing this date, honouring where the week starts. */
export function startOfWeek(iso: string, weekStartsOn: WeekStart = DEFAULT_WEEK_START): string {
  const d = fromIsoDate(iso);
  const day = d.getDay(); // 0 = Sunday
  const diff = -((day - weekStartsOn + 7) % 7);
  d.setDate(d.getDate() + diff);
  return toIsoDate(d);
}

export function weekDays(iso: string, weekStartsOn: WeekStart = DEFAULT_WEEK_START): string[] {
  const first = startOfWeek(iso, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

export function isToday(iso: string): boolean {
  return iso === toIsoDate(new Date());
}

/** Ziua săptămânii, ISO: 1 = luni … 7 = duminică. Aceeași numerotare ca a API-ului. */
export function isoWeekday(iso: string): number {
  const day = fromIsoDate(iso).getDay();
  return day === 0 ? 7 : day;
}

export function isWeekend(iso: string): boolean {
  const day = fromIsoDate(iso).getDay();
  return day === 0 || day === 6;
}

/** An appointment placed in a column, with the overlap layout resolved. */
export interface PositionedAppointment {
  appointment: Appointment;
  top: number;
  height: number;
  /** 0..1 of the column width. */
  left: number;
  width: number;
  /** Cascade mode only: how many 26px steps this card is inset and lifted. */
  depth?: number;
  /**
   * Cascade mode only: another card is drawn over this one's bottom-right
   * corner — the corner the status marker sits in.
   *
   * Cardul de dedesubt rămâne cu numele vizibil, fiindcă cel de deasupra
   * începe cu cel puțin zece minute mai jos, dar pierde exact colțul în care
   * scrie ce status are. Cine se uită la ziua asta nu poate spune dacă
   * programarea e cerută, confirmată sau deja trecută.
   */
  covered?: boolean;
}

/**
 * Lay out a day's appointments, side by side where they overlap.
 *
 * Bookings are grouped into clusters of mutually overlapping blocks, and each
 * cluster is divided into as many lanes as its widest point needs. Two
 * appointments at ten o'clock therefore each take half the column instead of
 * being drawn on top of each other — which is how the original hid
 * double-bookings until someone opened the record.
 */
export function layoutDay(appointments: Appointment[], zoom = 1): PositionedAppointment[] {
  const sorted = [...appointments].sort(
    (a, b) => a.startMinutes - b.startMinutes || b.endMinutes - a.endMinutes,
  );

  const positioned: PositionedAppointment[] = [];
  let cluster: Appointment[] = [];
  let clusterEnd = -1;

  const flush = () => {
    if (cluster.length === 0) return;

    // Greedy lane assignment: reuse the first lane whose last booking has
    // already finished.
    const laneEnds: number[] = [];
    const laneOf = new Map<string, number>();

    for (const appt of cluster) {
      let lane = laneEnds.findIndex((end) => end <= appt.startMinutes);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(appt.endMinutes);
      } else {
        laneEnds[lane] = appt.endMinutes;
      }
      laneOf.set(appt.id, lane);
    }

    const lanes = laneEnds.length;
    for (const appt of cluster) {
      const lane = laneOf.get(appt.id) ?? 0;
      positioned.push({
        appointment: appt,
        top: (appt.startMinutes - GRID_START) * PX_PER_MIN * zoom,
        // A floor of 18px: a 5-minute booking must still be clickable.
        height: Math.max(18, appt.dur * PX_PER_MIN * zoom),
        left: lane / lanes,
        width: 1 / lanes,
      });
    }

    cluster = [];
    clusterEnd = -1;
  };

  for (const appt of sorted) {
    if (cluster.length > 0 && appt.startMinutes >= clusterEnd) flush();
    cluster.push(appt);
    clusterEnd = Math.max(clusterEnd, appt.endMinutes);
  }
  flush();

  return positioned;
}

/** Snap a pixel offset inside the grid to the nearest 15-minute slot. */
export function pixelToSlot(offsetPx: number, zoom = 1): { hour: number; min: number } {
  const minutes = GRID_START + offsetPx / (PX_PER_MIN * zoom);
  const snapped = Math.round(minutes / 15) * 15;
  const clamped = Math.min(Math.max(snapped, GRID_START), GRID_END - 15);
  return { hour: Math.floor(clamped / 60), min: clamped % 60 };
}

/** The hour lines drawn behind the grid. */
export function hourMarks(): number[] {
  const marks: number[] = [];
  for (let m = GRID_START; m <= GRID_END; m += 60) marks.push(m);
  return marks;
}

/**
 * The old system's cascade: the long booking sits at the back on the full
 * width; anything that starts visibly later (over 10 minutes) and overlaps
 * it draws on top, inset one step to the right per level. Ported verbatim —
 * a 15-minute consult no longer gets the same width as a 3-hour case, so
 * the day's frame stays readable.
 */
const CASC_STEP_MIN = 10;

/**
 * Cât loc cere semnul de status în colț, în pixeli.
 *
 * Se măsoară în pixelii așezării, nu în minute — și asta a fost greșeala
 * primei variante. Scara zilei **comprimă** orele din afara programului, deci
 * o programare care se termină cu treizeci de minute mai devreme poate ajunge
 * la șase pixeli deasupra: în vederea Săptămână, „Elena Vieru 17:30–18:30"
 * acoperea colțul unei programări de 17:00–19:00, fiindcă jumătatea de oră
 * dintre 18:30 și 19:00 e după program și se strânge la nimic.
 */
const CASC_MARK_PX = 14;

/**
 * Cui i se acoperă colțul din dreapta-jos — semnul de status.
 *
 * În cascadă cardul din față e decalat cu un pas la stânga și se întinde spre
 * dreapta, deci acoperă tocmai colțul acela. Cardul de dedesubt rămâne cu
 * numele la vedere, fiindcă cel de deasupra începe mai jos, și fără nicio
 * urmă a statusului.
 *
 * Se cere **după** ce pozițiile sunt definitive: aceeași zi așezată pe scara
 * comprimată dă alte înălțimi, iar întrebarea e despre pixeli, nu despre ore.
 *
 * Comparația pe orizontală se face în fracțiuni de coloană și lasă decalajul
 * în pixeli afară — el e în pixeli, iar lățimea coloanei nu se cunoaște aici.
 * Când asta greșește, greșește într-o singură direcție: mută semnul pe fâșia
 * din stânga a unui card care avea și colțul liber. Fâșia aceea nu se acoperă
 * niciodată, deci prețul e o poziție neobișnuită, nu un status pierdut.
 */
function markCovered(list: PositionedAppointment[]): PositionedAppointment[] {
  for (const p of list) {
    const bottom = p.top + p.height;
    const right = p.left + p.width;
    p.covered = list.some(
      (o) =>
        o !== p &&
        (o.depth ?? 0) > (p.depth ?? 0) &&
        o.top < bottom - 1 &&
        o.top + o.height >= bottom - CASC_MARK_PX &&
        o.left < right - 1e-6 &&
        o.left + o.width >= right - 1e-6,
    );
  }
  return list;
}

export function layoutDayCascade(appointments: Appointment[], zoom = 1): PositionedAppointment[] {
  type Ev = {
    a: Appointment;
    s: number;
    e: number;
    depth: number;
    col: number;
    total: number;
  };
  const A: Ev[] = [...appointments]
    .map((a) => ({ a, s: a.startMinutes, e: a.endMinutes, depth: 0, col: 0, total: 1 }))
    .sort((p, q) => p.s - q.s || q.e - q.s - (p.e - p.s));
  const ov = (x: Ev, y: Ev) => x.s < y.e && y.s < x.e;

  const out: PositionedAppointment[] = [];
  const place = (ev: Ev) =>
    out.push({
      appointment: ev.a,
      top: (ev.s - GRID_START) * PX_PER_MIN * zoom,
      height: Math.max(18, (ev.e - ev.s) * PX_PER_MIN * zoom),
      left: ev.col / ev.total,
      width: 1 / ev.total,
      depth: ev.depth,
    });

  let i = 0;
  while (i < A.length) {
    let clusterEnd = A[i].e;
    let j = i + 1;
    while (j < A.length && A[j].s < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, A[j].e);
      j++;
    }
    const cl = A.slice(i, j);
    if (cl.length === 1) {
      place(cl[0]);
      i = j;
      continue;
    }

    // A booking goes ON TOP of another only when it starts visibly later;
    // at an equal start the small one would cover exactly the patient's
    // name in the big one, so equal starts stay side by side instead.
    const placed: Ev[] = [];
    for (const ev of cl) {
      let d = 0;
      for (const p of placed) {
        if (ov(p, ev) && ev.s - p.s >= CASC_STEP_MIN) d = Math.max(d, p.depth + 1);
      }
      ev.depth = d;
      placed.push(ev);
    }

    // Same level + same start: share the remaining width in columns.
    const byDepth = new Map<number, Ev[]>();
    for (const ev of cl) {
      const list = byDepth.get(ev.depth) ?? [];
      list.push(ev);
      byDepth.set(ev.depth, list);
    }
    for (const evs of byDepth.values()) {
      const cols: Ev[][] = [];
      for (const ev of evs) {
        let put = false;
        for (let k = 0; k < cols.length; k++) {
          if (!cols[k].some((o) => ov(o, ev))) {
            cols[k].push(ev);
            ev.col = k;
            put = true;
            break;
          }
        }
        if (!put) {
          ev.col = cols.length;
          cols.push([ev]);
        }
      }
      for (const ev of evs) ev.total = cols.length;
    }

    cl.forEach(place);
    i = j;
  }
  return markCovered(out);
}

/**
 * The old grid's trick, ported: hours outside the clinic's programme exist
 * but are COMPRESSED — a thin strip two hours either side, so 07:00 is
 * reachable without stealing space from the working day.
 */
export interface DayScale {
  start: number;
  end: number;
  height: number;
  yOf(minute: number): number;
  minuteAt(y: number): number;
}

const COMPRESSED_PX_PER_MIN = 0.2;

/**
 * Ziua, ca scară de pixeli.
 *
 * `fillHeight` e înălțimea vizibilă a grilei, când ecranul o poate spune.
 * Referința face același lucru (`setPHFill`): dacă ziua e mai scurtă decât
 * spațiul, **orele lucrătoare se întind** ca să-l umple — altfel un ecran
 * înalt arată programul terminat la 18:00 și o treime de card alb sub el, iar
 * spațiul dat de monitor nu se transformă în lizibilitate.
 *
 * Se întinde numai în sus: dacă ziua e deja mai înaltă decât spațiul (zoom
 * mărit, program lung), scara rămâne a ei și grila derulează. Și numai orele
 * **active** cresc — cele comprimate din afara programului rămân fâșia
 * subțire care sunt, altfel „două ore înainte de deschidere" ar mânca jumătate
 * de ecran pe un monitor mare.
 */
export function buildDayScale(
  openMin: number,
  closeMin: number,
  zoom = 1,
  fillHeight?: number,
): DayScale {
  const start = Math.max(0, openMin - 120);
  const end = Math.min(24 * 60, closeMin + 120);
  let active = PX_PER_MIN * zoom;
  const thin =
    COMPRESSED_PX_PER_MIN * zoom * PX_PER_MIN * 60 > 0 ? COMPRESSED_PX_PER_MIN * zoom : 0.2;

  // Cât cer capetele comprimate, orice s-ar întâmpla cu mijlocul.
  const edges = (openMin - start) * thin + (end - closeMin) * thin;
  const activeMinutes = Math.max(0, closeMin - openMin);
  if (fillHeight && fillHeight > 0 && activeMinutes > 0) {
    const needed = (fillHeight - edges) / activeMinutes;
    if (needed > active) active = needed;
  }

  const segs: Array<{ from: number; to: number; px: number }> = [
    { from: start, to: openMin, px: thin },
    { from: openMin, to: closeMin, px: active },
    { from: closeMin, to: end, px: thin },
  ].filter((s) => s.to > s.from);

  const yOf = (minute: number) => {
    let y = 0;
    const m = Math.min(end, Math.max(start, minute));
    for (const seg of segs) {
      if (m <= seg.from) break;
      y += (Math.min(m, seg.to) - seg.from) * seg.px;
    }
    return y;
  };
  const height = yOf(end);
  const minuteAt = (y: number) => {
    let acc = 0;
    for (const seg of segs) {
      const segH = (seg.to - seg.from) * seg.px;
      if (y <= acc + segH) return seg.from + (y - acc) / seg.px;
      acc += segH;
    }
    return end;
  };
  return { start, end, height, yOf, minuteAt };
}

/** Pasul grilei: un sfert de oră, ca pe blocul de programări al recepției. */
export const QUARTER = 15;
/** Sub atâția pixeli o linie de sfert nu mai separă nimic. */
export const MIN_LINE_GAP = 8;
/** Înălțimea unei cifre din rigla din stânga, plus aer. */
export const MIN_LABEL_GAP = 14;

export interface GridMark {
  /** Minutul de la miezul nopții. */
  m: number;
  /** Unde cade pe scara zilei, în pixeli. */
  y: number;
  kind: 'hour' | 'half' | 'quarter';
  /** Dacă i se scrie ora în rigla din stânga. */
  label: boolean;
}

/**
 * Liniile și cifrele grilei, pentru o scară dată.
 *
 * Ziua n-are o singură densitate: orele din afara programului sunt comprimate
 * într-o fâșie (vezi `buildDayScale`), unde o oră întreagă are 12px și un
 * sfert trei. Deci pasul nu se poate alege o dată pentru toată ziua — se
 * desenează cât încape, măsurat în pixeli, nu în minute.
 *
 * De aici cele două praguri. Un sfert se desenează numai dacă acolo un sfert
 * are `MIN_LINE_GAP` — măsura e pasul de dinaintea lui, nu distanța până la
 * ultima linie desenată, altfel în fâșie al treilea sfert ajunge la 9px de oră
 * și trece singur de prag: ies două linii pe oră, la 45 de minute după fiecare,
 * adică o riglă care nu măsoară nimic. Iar o cifră cere `MIN_LABEL_GAP` de la
 * cifra dinaintea ei — asta repară ce se vedea pe telefon, 07:00, 08:00 și
 * 09:00 scrise una peste alta la 12px distanță. Rămân capetele fâșiei, care
 * sunt și singurele care spun ceva.
 */
export function gridMarks(scale: DayScale): GridMark[] {
  const out: GridMark[] = [];
  let lastLabel = -Infinity;
  for (let m = Math.ceil(scale.start / QUARTER) * QUARTER; m <= scale.end; m += QUARTER) {
    const y = scale.yOf(m);
    const hour = m % 60 === 0;
    if (!hour && y - scale.yOf(m - QUARTER) < MIN_LINE_GAP) continue;
    const label = hour && y - lastLabel >= MIN_LABEL_GAP;
    if (label) lastLabel = y;
    out.push({ m, y, kind: hour ? 'hour' : m % 30 === 0 ? 'half' : 'quarter', label });
  }
  return out;
}

/** Position a day's bookings on a piecewise scale, columns or cascade. */
export function layoutDayScaled(
  appointments: Appointment[],
  scale: DayScale,
  mode: 'coloane' | 'cascada',
): PositionedAppointment[] {
  const base = mode === 'cascada' ? layoutDayCascade(appointments, 1) : layoutDay(appointments, 1);
  /* Din nou, nu moștenit: scara comprimă orele din afara programului, deci
     aceleași două programări se acoperă altfel decât o făceau la scara
     dreaptă. Întrebarea e despre pixelii de pe ecran. */
  return markCovered(
    base.map((p) => ({
      ...p,
      top: scale.yOf(p.appointment.startMinutes),
      height: Math.max(
        18,
        scale.yOf(p.appointment.endMinutes) - scale.yOf(p.appointment.startMinutes),
      ),
    })),
  );
}

/**
 * The old system's convention, kept as-is: a booking whose service or
 * patient name starts with "pauz/blocat/block" is a schedule block, not a
 * patient — drawn hatched and left out of the day's counts.
 */
/**
 * The word-starts that mark a booking as a schedule block rather than a patient.
 *
 * "Eveniment" is in the list because it is the same thing: an interval with no
 * patient in it. A meeting, a courier, a training session — the chair is busy,
 * nobody is being treated, and the day's count must not include it. A clinic
 * that words its blocks differently ("rezervat", "prânz", "training") passes its
 * own set; omitted, the reference default stands.
 */
export const DEFAULT_BLOCK_KEYWORDS = ['pauz', 'blocat', 'block', 'eveniment'] as const;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function blockRe(keywords: readonly string[]): RegExp {
  const cleaned = keywords.map((k) => k.trim()).filter((k) => k.length > 0);
  const words = cleaned.length > 0 ? cleaned : DEFAULT_BLOCK_KEYWORDS;
  return new RegExp(`^(${words.map(escapeRe).join('|')})`, 'i');
}

export function isBlockAppt(
  a: Pick<Appointment, 'service' | 'patientName'>,
  keywords: readonly string[] = DEFAULT_BLOCK_KEYWORDS,
): boolean {
  const rx = blockRe(keywords);
  return rx.test((a.service ?? '').trim()) || rx.test((a.patientName ?? '').trim());
}

/**
 * Minutes past its start after which a booking nobody has arrived for asks
 * reception to phone.
 */
export const OVERDUE_AFTER_MIN = 15;

/** Nobody has come, and nobody has said they are coming. */
const OVERDUE_STATUSES = new Set<string>(['pending', 'scheduled', 'confirmed']);

/**
 * What on a card asks for somebody's eye right now.
 *
 * - `overdue` — today, fifteen minutes past the start, and the status still
 *   says the patient is expected. Any other status ends it: „Întârzie” means
 *   reception has already called, „Sosit” that there is nothing to call about.
 * - `waiting` — the patient is in the waiting room, for as long as they are.
 *
 * Only today: yesterday's unmarked booking is a no-show to record, not a
 * phone call to make, and a pulsing past would drown the one that matters.
 */
export type CardAttention = 'overdue' | 'waiting' | null;

export function cardAttention(
  a: Pick<Appointment, 'status' | 'apptDate' | 'startMinutes' | 'service' | 'patientName'>,
  now: Date,
  opts: { overdueMin?: number; blockKeywords?: readonly string[] } = {},
): CardAttention {
  const overdueMin = Number.isFinite(opts.overdueMin)
    ? (opts.overdueMin as number)
    : OVERDUE_AFTER_MIN;
  if (isBlockAppt(a, opts.blockKeywords ?? DEFAULT_BLOCK_KEYWORDS)) return null;
  if (a.status === 'waiting') return 'waiting';
  if (!OVERDUE_STATUSES.has(a.status) || a.apptDate !== toIsoDate(now)) return null;
  const minutes = now.getHours() * 60 + now.getMinutes();
  return minutes >= a.startMinutes + overdueMin ? 'overdue' : null;
}

/** A schedule block, as much of it as the rules below need. */
export interface SlotBlock {
  name: string | null;
  allowOnly: string | null;
  from: string;
  to: string;
}

/**
 * What a slot allows to be booked in it, if anything.
 *
 * A block may carry `allowOnly` — „doar Consultații". The grid already draws
 * that on the slot's pill; this is what makes it mean something when somebody
 * books: the dialog narrows its service list to the matching ones and says so.
 *
 * It is a scheduling rule, not a permission — the dialog offers „Arată toate"
 * beside it, because the clinic that reserved a morning for consultations is
 * also the clinic that squeezes an emergency into it. That is why it lives in
 * the browser and not behind a guard.
 */
export function allowOnlyAt(blocks: SlotBlock[], minutes: number): string | null {
  for (const b of blocks) {
    const only = b.allowOnly?.trim();
    if (!only || !b.from || !b.to) continue;
    if (minutes >= parseTime(b.from) && minutes < parseTime(b.to)) return only;
  }
  return null;
}

/**
 * The named slot — the „cabinet" — an appointment starts in.
 *
 * The reference derives a booking's room from the block it begins inside, and
 * two bookings in the same room at the same time are a clash however different
 * their doctors are. A block with no name divides nothing; it is simply an
 * hour the doctor is not available.
 */
export function slotNameAt(blocks: SlotBlock[], minutes: number): string | null {
  for (const b of blocks) {
    const name = b.name?.trim();
    if (!name || !b.from || !b.to) continue;
    if (minutes >= parseTime(b.from) && minutes < parseTime(b.to)) return name;
  }
  return null;
}

/** Ce cabinet ocupă o programare, și dacă se bate cu alta pe el. */
export interface CabinetUse {
  name: string;
  clash: boolean;
  /**
   * Culoarea cabinetului — pusă doar când medicul lucrează în ≥2 cabinete în
   * ziua aceea, ca cele două încăperi să se distingă în coloana lui. Absentă
   * pentru un singur cabinet, unde nu e nimic de deosebit.
   */
  color?: string;
}

/** Paleta cabinetelor — culori clare, distincte de cele ale serviciilor. */
const CABINET_PALETTE = [
  '#2563eb',
  '#0d9488',
  '#d97706',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#65a30d',
  '#dc2626',
];

/**
 * O culoare stabilă pentru un nume de cabinet: același nume dă mereu aceeași
 * culoare, deci „Cabinet 1" arată la fel oriunde apare. Hash-ul e cel al
 * serviciilor (`h = h * 31 + char`, fără semn).
 */
export function cabinetColorFor(name: string | null | undefined): string {
  const key = String(name || 'cabinet')
    .trim()
    .toLowerCase();
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return CABINET_PALETTE[h % CABINET_PALETTE.length];
}

/**
 * Cabinetul unei programări, dedus din slotul în care începe.
 *
 * Un bloc de program cu nume — „Cabinet 3" — împarte ziua unui medic în
 * încăperi. Programarea care începe înăuntrul lui se face acolo, iar **două
 * programări în aceeași încăpere la aceeași oră sunt o ciocnire oricât de
 * diferiți ar fi medicii**: încăperea e una singură. Asta e deosebirea față de
 * suprapunerea obișnuită, care e a unui singur medic cu el însuși.
 *
 * Numele se compară fără majuscule și fără spații la capete, fiindcă îl scrie
 * un om în două ferestre diferite. Un bloc fără nume nu împarte nimic — e o
 * oră în care medicul nu e disponibil, și atât.
 */
export function cabinetMap(
  columns: Array<{ date: string; doctorId: string; blocks: SlotBlock[] }>,
  appts: Array<{
    id: string;
    apptDate: string;
    doctorId: string;
    startMinutes: number;
    endMinutes: number;
  }>,
): Map<string, CabinetUse> {
  const byColumn = new Map<string, SlotBlock[]>();
  for (const c of columns) byColumn.set(`${c.date}__${c.doctorId}`, c.blocks);

  // Coloanele cu ≥2 cabinete distincte: acolo colorăm încăperile, ca să se vadă
  // că medicul lucrează în două. Un singur cabinet nu are ce deosebi.
  const multiColumn = new Set<string>();
  for (const c of columns) {
    const names = new Set(
      c.blocks.map((b) => b.name?.trim().toLowerCase()).filter((n): n is string => Boolean(n)),
    );
    if (names.size >= 2) multiColumn.add(`${c.date}__${c.doctorId}`);
  }

  const used: Array<{
    id: string;
    key: string;
    from: number;
    to: number;
    use: CabinetUse;
  }> = [];
  const out = new Map<string, CabinetUse>();

  for (const a of appts) {
    const blocks = byColumn.get(`${a.apptDate}__${a.doctorId}`);
    if (!blocks) continue;
    const name = slotNameAt(blocks, a.startMinutes);
    if (!name) continue;
    const colKey = `${a.apptDate}__${a.doctorId}`;
    const use: CabinetUse = {
      name,
      clash: false,
      color: multiColumn.has(colKey) ? cabinetColorFor(name) : undefined,
    };
    out.set(a.id, use);
    used.push({
      id: a.id,
      key: `${a.apptDate}__${name.toLowerCase()}`,
      from: a.startMinutes,
      to: a.endMinutes,
      use,
    });
  }

  for (let i = 0; i < used.length; i++) {
    for (let j = i + 1; j < used.length; j++) {
      const x = used[i];
      const y = used[j];
      if (x.key !== y.key) continue;
      if (x.from < y.to && y.from < x.to) {
        x.use.clash = true;
        y.use.clash = true;
      }
    }
  }

  return out;
}

/**
 * What a booking is for, one line per procedure, as the calendar writes it.
 *
 * It named the first procedure and added „+N” for the rest, so a visit where a
 * filling and a consultation were done read „Obturație (plombă) +1” beside the
 * price of both, and the calendar never said what the second one was. A booking
 * with no plan rows is what its service says.
 */
export function procedureLines(a: Pick<Appointment, 'service' | 'procedures'>): string[] {
  if (a.procedures.length === 0) return a.service ? [a.service] : [];
  return a.procedures.map((p) => {
    const name = p.name?.trim() || 'procedură nescrisă';
    return p.teeth ? `${name} · ${p.teeth}` : name;
  });
}
